import React, { useState, useEffect, useCallback } from 'react';
import { ArrowDownToLine, Plus, Search, Loader2, Eye, X, ArrowLeft, Package, CheckCircle, XCircle, FileText, Upload, Paperclip, Download, Trash2, DollarSign } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { inwardAPI, branchAPI, fetchPaged } from '../../services/api';
import { filterBranchesForUser } from '../../utils/branchScope';
import api from '../../services/api';
import { toast } from 'sonner';
import Pagination from '../ui/Pagination';
import SortableHeader from '../ui/SortableHeader';

const InwardModule = () => {
  const [view, setView] = useState('list'); // list | create | detail
  const [inwards, setInwards] = useState([]);
  const [stats, setStats] = useState({ total: 0, completed: 0, cancelled: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', order: 'desc' });
  const handlePageSizeChange = (v) => { setPageSize(v); setPage(1); };
  const handleSort = (key, order) => { setSortConfig({ key, order }); setPage(1); };

  const [selectedInward, setSelectedInward] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({ number: '', date: '', url: '', name: '' });
  const location = useLocation();

  // Create form state
  const [branches, setBranches] = useState([]);
  const [poSearch, setPOSearch] = useState('');
  const [poResults, setPOResults] = useState([]);
  const [poLoading, setPOLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = React.useRef(false);  // Sync double-click guard (state is async)
  const [form, setForm] = useState({
    po_reference: '', po_number: '', vendor_id: '', vendor_name: '', vendor_gst: '',
    branch: '', inward_date: new Date().toISOString().slice(0, 10),
    items: [], notes: '',
    inward_freight: 0, loading_charges: 0, unloading_charges: 0,
    additional_charges: [],
    po_type: 'domestic', po_currency: 'INR', po_exchange_rate: 1,
    exchange_rate: 1,
  });

  // Attachment state
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    const newAttachments = [...attachments];
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 10MB limit`);
        continue;
      }
      newAttachments.push({ filename: file.name, content_type: file.type, size: file.size, _file: file, _local: true });
    }
    setAttachments(newAttachments);
    setUploading(false);
    e.target.value = '';
  };

  const removeAttachment = (idx) => {
    setAttachments(prev => prev.filter((_, i) => i !== idx));
  };

  const downloadAttachment = async (att) => {
    try {
      const resp = await api.get('/files/download', { params: { path: att.storage_path }, responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url; a.download = att.filename || 'file'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  // Fetch list
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, page_size: pageSize };
      if (search) params.search = search;
      if (sortConfig.key) { params.sort_by = sortConfig.key; params.sort_order = sortConfig.order; }
      const [result, st] = await Promise.all([fetchPaged('/inwards', params), inwardAPI.getStats()]);
      setInwards(result.items);
      setTotal(result.total);
      setTotalPages(result.total_pages);
      setStats(st);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, page, pageSize, sortConfig]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Handle deep-link from ?view= query param (e.g., from Item History)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const viewId = params.get('view');
    if (viewId) {
      inwardAPI.getById(viewId).then(data => {
        setSelectedInward(data);
        setView('detail');
      }).catch(() => {});
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [location.search, location.pathname]);

  // Fetch branches
  useEffect(() => {
    branchAPI.getAll().then(data => {
      const items = Array.isArray(data) ? data : (data?.items || []);
      setBranches(items);
    }).catch(() => {});
  }, []);

  // PO search with debounce
  const searchPORef = React.useRef();
  const handlePOSearch = (val) => {
    setPOSearch(val);
    clearTimeout(searchPORef.current);
    if (val.length < 2) { setPOResults([]); return; }
    searchPORef.current = setTimeout(async () => {
      setPOLoading(true);
      try {
        const res = await fetchPaged('/purchase', { search: val, page: 1, page_size: 10 });
        // Filter out Draft and Cancelled POs — only approved/active POs for inward
        const activePOs = (res.items || []).filter(po => po.status !== 'Draft' && po.status !== 'Cancelled');
        setPOResults(activePOs);
      } catch { setPOResults([]); }
      setPOLoading(false);
    }, 300);
  };

  // PO select — auto-fill everything
  const handlePOSelect = async (po) => {
    setPOSearch(po.po_number);
    setPOResults([]);
    setPOLoading(true);
    try {
      const data = await inwardAPI.poLookup(po.id);
      setForm({
        ...form,
        po_reference: data.po_id,
        po_number: data.po_number,
        vendor_id: data.vendor_id,
        vendor_name: data.vendor_name,
        vendor_gst: data.vendor_gst,
        branch: data.branch || form.branch,
        items: data.items,
        po_type: data.po_type || 'domestic',
        po_currency: data.po_currency || 'INR',
        po_exchange_rate: data.po_exchange_rate || 1,
        exchange_rate: data.po_exchange_rate || 1,
      });
    } catch (err) {
      toast.error('Failed to load PO details');
    }
    setPOLoading(false);
  };

  const clearPO = () => {
    setPOSearch('');
    setForm({ ...form, po_reference: '', po_number: '', vendor_id: '', vendor_name: '', vendor_gst: '', items: [] });
  };

  // Update inward qty for an item
  const updateItemQty = (idx, field, value) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: value };
    setForm({ ...form, items });
  };

  // Submit
  const handleSubmit = async () => {
    if (submittingRef.current) return; // Sync guard: prevents double-click duplicates
    if (!form.po_reference) { toast.error('Please select a Purchase Order'); return; }
    if (!form.branch) { toast.error('Branch is mandatory'); return; }
    if (!form.inward_date) { toast.error('Please enter inward date'); return; }
    const hasQty = form.items.some(i => i.inward_qty > 0);
    if (!hasQty) { toast.error('At least one item must have inward quantity > 0'); return; }
    if (attachments.length === 0) { toast.error('Purchase Invoice / Attachment is mandatory. Please upload at least one file.'); return; }
    // Import PO: validate exchange rate
    if (form.po_type === 'import' && form.po_currency !== 'INR' && (!form.exchange_rate || form.exchange_rate <= 0)) {
      toast.error('Exchange rate is mandatory for import POs'); return;
    }

    submittingRef.current = true;
    try {
      setSubmitting(true);
      const result = await inwardAPI.create(form);
      const inwardId = result.id;

      // Upload attachments if any
      if (attachments.length > 0 && inwardId) {
        for (const att of attachments) {
          if (att._local && att._file) {
            const fd = new FormData();
            fd.append('file', att._file);
            await api.post(`/files/upload?entity_type=inward&entity_id=${inwardId}`, fd, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
          }
        }
      }

      toast.success('Inward created successfully! Stock entries auto-generated.');
      setView('list');
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create inward');
    } finally { setSubmitting(false); submittingRef.current = false; }
  };

  const resetForm = () => {
    setForm({ po_reference: '', po_number: '', vendor_id: '', vendor_name: '', vendor_gst: '', branch: '', inward_date: new Date().toISOString().slice(0, 10), items: [], notes: '', inward_freight: 0, loading_charges: 0, unloading_charges: 0, additional_charges: [], po_type: 'domestic', po_currency: 'INR', po_exchange_rate: 1, exchange_rate: 1 });
    setPOSearch('');
    setAttachments([]);
  };

  // View detail
  const viewDetail = async (id) => {
    try {
      const data = await inwardAPI.getById(id);
      setSelectedInward(data);
      setView('detail');
    } catch { toast.error('Failed to load inward'); }
  };

  // Detail view
  if (view === 'detail' && selectedInward) {
    return (
      <div className="p-5 lg:p-6" data-testid="inward-detail">
        <button onClick={() => { setView('list'); setSelectedInward(null); }} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-900 mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Inwards
        </button>
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-bold text-gray-900" data-testid="inward-detail-number">{selectedInward.inward_number}</h2>
              <p className="text-xs text-gray-500">Created {new Date(selectedInward.created_at).toLocaleDateString()}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 text-xs font-bold rounded-full ${selectedInward.status === 'Completed' ? 'bg-green-100 text-green-700' : selectedInward.status === 'Cancelled' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
                {selectedInward.status}
              </span>
              {selectedInward.status === 'Completed' && (
                <button
                  onClick={async () => {
                    if (!window.confirm('Cancel this inward? All stock entries created by this inward will be reversed.')) return;
                    try {
                      const updated = await inwardAPI.cancel(selectedInward.id);
                      setSelectedInward(updated);
                      toast.success('Inward cancelled. Stock entries reversed.');
                      fetchData();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Failed to cancel inward');
                    }
                  }}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                  data-testid="cancel-inward-btn"
                >
                  <XCircle className="w-3.5 h-3.5" /> Cancel Inward
                </button>
              )}
            </div>
          </div>

          {/* Cancellation banner */}
          {selectedInward.status === 'Cancelled' && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3" data-testid="inward-cancelled-banner">
              <XCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs">
                <p className="font-semibold text-red-800">Inward Cancelled</p>
                <p className="text-red-600">{selectedInward.stock_entries_reversed || 0} stock entries reversed. Cancelled by {selectedInward.cancelled_by_name || 'Unknown'} on {selectedInward.cancelled_at ? new Date(selectedInward.cancelled_at).toLocaleString() : '-'}</p>
              </div>
            </div>
          )}

          {/* Info Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'PO Number', value: selectedInward.po_number },
              { label: 'Vendor', value: selectedInward.vendor_name },
              { label: 'Vendor GST', value: selectedInward.vendor_gst || '—' },
              { label: 'Branch', value: selectedInward.branch_name || selectedInward.branch },
              { label: 'Inward Date', value: selectedInward.inward_date },
            ].map(f => (
              <div key={f.label} className="bg-gray-50 rounded-lg p-3">
                <p className="text-[10px] text-gray-500 mb-0.5">{f.label}</p>
                <p className="text-xs font-semibold text-gray-900">{f.value}</p>
              </div>
            ))}
          </div>

          {/* Items table */}
          <div className="border border-gray-100 rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-gray-50">
                <tr>
                  {['Product', 'Unit', 'Ordered Qty', 'Previously Received', 'Rest Qty', 'Inward Qty', 'Batch', 'Rate', 'Freight/Unit', 'Landed Cost'].map(h => (
                    <th key={h} className="text-left px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(selectedInward.items || []).map((item, idx) => {
                  const computedRest = Math.max(0, (item.ordered_qty || 0) - (item.previously_received || 0) - (item.inward_qty || 0));
                  return (
                  <tr key={idx} className="border-t border-gray-50">
                    <td className="px-3 py-2.5 font-medium text-gray-900">{item.product_name}</td>
                    <td className="px-3 py-2.5 text-gray-600">{item.unit}</td>
                    <td className="px-3 py-2.5 text-gray-600">{item.ordered_qty}</td>
                    <td className="px-3 py-2.5 text-gray-600">{item.previously_received}</td>
                    <td className="px-3 py-2.5 text-amber-600 font-semibold">{computedRest}</td>
                    <td className="px-3 py-2.5 text-green-700 font-bold">{item.inward_qty}</td>
                    <td className="px-3 py-2.5 text-gray-500 font-mono text-[10px]">{item.batch || '—'}</td>
                    <td className="px-3 py-2.5 text-gray-600">₹{item.cost_price?.toLocaleString()}</td>
                    <td className="px-3 py-2.5 text-amber-600">{item.freight_per_unit ? `₹${item.freight_per_unit}` : '—'}</td>
                    <td className="px-3 py-2.5 text-green-700 font-bold">{item.landed_cost_per_unit ? `₹${item.landed_cost_per_unit}` : `₹${item.cost_price?.toLocaleString()}`}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Freight & Charges Summary */}
          {(selectedInward.inward_freight > 0 || selectedInward.total_additional > 0) && (
            <div className="mt-4 bg-amber-50/50 border border-amber-100 rounded-lg p-4">
              <h4 className="text-[10px] font-bold text-amber-700 uppercase mb-2">Freight & Charges</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                {selectedInward.inward_freight > 0 && <div><span className="text-gray-500">Freight:</span> <span className="font-bold">₹{selectedInward.inward_freight?.toLocaleString()}</span></div>}
                {selectedInward.loading_charges > 0 && <div><span className="text-gray-500">Loading:</span> <span className="font-bold">₹{selectedInward.loading_charges?.toLocaleString()}</span></div>}
                {selectedInward.unloading_charges > 0 && <div><span className="text-gray-500">Unloading:</span> <span className="font-bold">₹{selectedInward.unloading_charges?.toLocaleString()}</span></div>}
                {(selectedInward.additional_charges || []).map((ch, i) => (
                  <div key={i}><span className="text-gray-500">{ch.name}:</span> <span className="font-bold">₹{ch.amount?.toLocaleString()}</span></div>
                ))}
              </div>
              <div className="mt-2 pt-2 border-t border-amber-200 flex gap-6 text-xs">
                <div><span className="text-gray-500">Total Charges:</span> <span className="font-bold text-amber-700">₹{selectedInward.total_additional?.toLocaleString()}</span></div>
                <div><span className="text-gray-500">Total Landed Cost:</span> <span className="font-bold text-green-700">₹{selectedInward.total_landed_cost?.toLocaleString()}</span></div>
              </div>
            </div>
          )}

          {selectedInward.notes && (
            <div className="mt-4 bg-gray-50 rounded-lg p-3">
              <p className="text-[10px] text-gray-500 mb-0.5">Notes</p>
              <p className="text-xs text-gray-700">{selectedInward.notes}</p>
            </div>
          )}

          {/* Purchase Invoice Section */}
          <div className="mt-4 bg-blue-50/30 border border-blue-100 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-[10px] font-bold text-blue-700 uppercase flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5" /> Purchase Invoice
              </h4>
              {selectedInward.purchase_invoice_number ? (
                <span className="px-2 py-0.5 text-[9px] font-bold bg-green-100 text-green-700 rounded-full" data-testid="invoice-status-badge">Invoiced</span>
              ) : (
                <span className="px-2 py-0.5 text-[9px] font-bold bg-amber-100 text-amber-700 rounded-full" data-testid="invoice-status-badge">Pending Invoice</span>
              )}
            </div>
            {!editingInvoice ? (
              <div>
                {selectedInward.purchase_invoice_number ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                    <div><span className="text-gray-500">Invoice No:</span> <span className="font-bold text-gray-900">{selectedInward.purchase_invoice_number}</span></div>
                    <div><span className="text-gray-500">Invoice Date:</span> <span className="font-semibold">{selectedInward.purchase_invoice_date || '—'}</span></div>
                    {selectedInward.purchase_invoice_document_url && (
                      <div>
                        <a href={selectedInward.purchase_invoice_document_url} target="_blank" rel="noopener noreferrer"
                          className="text-blue-600 hover:underline flex items-center gap-1" data-testid="view-purchase-invoice">
                          <Download className="w-3 h-3" /> View Invoice
                        </a>
                      </div>
                    )}
                    <div className="col-span-full text-[9px] text-gray-400">
                      Updated by {selectedInward.purchase_invoice_updated_by_name} on {new Date(selectedInward.purchase_invoice_updated_at).toLocaleDateString('en-IN')}
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">No purchase invoice added yet.</p>
                )}
                <button onClick={() => { setEditingInvoice(true); setInvoiceForm({ number: selectedInward.purchase_invoice_number || '', date: selectedInward.purchase_invoice_date || '', url: selectedInward.purchase_invoice_document_url || '', name: selectedInward.purchase_invoice_document_name || '' }); }}
                  className="mt-2 px-3 py-1.5 text-[11px] font-semibold text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors" data-testid="edit-invoice-btn">
                  {selectedInward.purchase_invoice_number ? 'Update Invoice' : 'Add Invoice'}
                </button>
              </div>
            ) : (
              <div className="space-y-2.5">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] text-gray-500 mb-0.5 block">Invoice Number *</label>
                    <input value={invoiceForm.number} onChange={e => setInvoiceForm(f => ({ ...f, number: e.target.value }))}
                      className="w-full px-3 py-1.5 text-xs border border-gray-200 rounded-lg" placeholder="Enter invoice number" data-testid="invoice-number-input" />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500 mb-0.5 block">Invoice Date</label>
                    <input type="date" value={invoiceForm.date} onChange={e => setInvoiceForm(f => ({ ...f, date: e.target.value }))}
                      className="w-full px-3 py-1.5 text-xs border border-gray-200 rounded-lg" data-testid="invoice-date-input" />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] text-gray-500 mb-0.5 block">Upload Invoice Document</label>
                  <input type="file" onChange={async (e) => {
                    const file = e.target.files[0]; if (!file) return;
                    try {
                      const { storageAPI } = await import('../../services/api');
                      const result = await storageAPI.upload(file);
                      setInvoiceForm(f => ({ ...f, url: result.url, name: file.name }));
                      toast.success('Invoice uploaded');
                    } catch { toast.error('Failed to upload'); }
                  }} className="w-full text-xs" data-testid="invoice-file-input" />
                  {invoiceForm.name && <p className="text-[10px] text-green-600 mt-1">File: {invoiceForm.name}</p>}
                </div>
                <div className="flex gap-2">
                  <button onClick={async () => {
                    if (!invoiceForm.number.trim()) { toast.error('Invoice number is required'); return; }
                    try {
                      const updated = await inwardAPI.updatePurchaseInvoice(selectedInward.id, {
                        purchase_invoice_number: invoiceForm.number,
                        purchase_invoice_date: invoiceForm.date,
                        purchase_invoice_document_url: invoiceForm.url,
                        purchase_invoice_document_name: invoiceForm.name,
                      });
                      setSelectedInward(updated);
                      setEditingInvoice(false);
                      toast.success('Purchase invoice updated');
                    } catch { toast.error('Failed to save invoice'); }
                  }} className="px-3 py-1.5 text-[11px] font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700" data-testid="save-invoice-btn">Save Invoice</button>
                  <button onClick={() => setEditingInvoice(false)} className="px-3 py-1.5 text-[11px] font-semibold text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
                </div>
              </div>
            )}
          </div>

          {/* Attachments */}
          {(selectedInward.attachments || []).length > 0 && (
            <div className="mt-4">
              <p className="text-[10px] font-semibold text-gray-500 uppercase mb-2">Purchase Invoice / Attachments</p>
              <div className="flex flex-wrap gap-2">
                {selectedInward.attachments.map((att, idx) => (
                  <button key={idx} onClick={() => downloadAttachment(att)} data-testid={`inward-attachment-${idx}`}
                    className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-200 transition-colors">
                    <Paperclip className="w-3.5 h-3.5 text-gray-400" />
                    <span className="text-xs text-gray-700 max-w-[180px] truncate">{att.filename}</span>
                    <Download className="w-3 h-3 text-blue-500" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Create form
  if (view === 'create') {
    return (
      <div className="p-5 lg:p-6" data-testid="inward-create">
        <button onClick={() => { setView('list'); resetForm(); }} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-900 mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Inwards
        </button>

        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-1" style={{ fontFamily: 'DM Sans' }}>New Inward</h2>
          <p className="text-xs text-gray-500 mb-5">Record goods received against a Purchase Order</p>

          {/* PO Search + Vendor Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
            <div>
              <label className="block text-[11px] font-medium text-gray-700 mb-1">Purchase Order # *</label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                <input
                  value={poSearch}
                  onChange={e => handlePOSearch(e.target.value)}
                  placeholder="Search PO number..."
                  disabled={!!form.po_reference}
                  data-testid="inward-po-search"
                  className={`w-full pl-8 pr-8 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200 ${form.po_reference ? 'bg-blue-50 border-blue-200' : ''}`}
                />
                {form.po_reference && (
                  <button onClick={clearPO} className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-gray-100">
                    <X className="w-3 h-3 text-gray-400" />
                  </button>
                )}
                {poLoading && <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 animate-spin text-gray-400" />}
                {poResults.length > 0 && !form.po_reference && (
                  <div className="absolute z-30 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-[200px] overflow-y-auto">
                    {poResults.map(po => (
                      <button key={po.id} onClick={() => handlePOSelect(po)} data-testid={`po-result-${po.id}`}
                        className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-gray-50 last:border-0">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-mono font-bold text-gray-900">{po.po_number}</span>
                          <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${po.status === 'Received' ? 'bg-green-100 text-green-700' : po.status === 'Partially Received' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>{po.status}</span>
                        </div>
                        <p className="text-[10px] text-gray-500 mt-0.5">{po.vendor_name} | {po.items?.length || 0} items</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-medium text-gray-700 mb-1">Vendor</label>
              <input value={form.vendor_name} disabled data-testid="inward-vendor" className="w-full px-3 py-2 text-xs bg-gray-50 border border-gray-200 rounded-lg text-gray-700" />
            </div>

            <div>
              <label className="block text-[11px] font-medium text-gray-700 mb-1">Vendor GST</label>
              <input value={form.vendor_gst} disabled data-testid="inward-vendor-gst" className="w-full px-3 py-2 text-xs bg-gray-50 border border-gray-200 rounded-lg text-gray-500 font-mono" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
            <div>
              <label className="block text-[11px] font-medium text-gray-700 mb-1">Branch *</label>
              <select value={form.branch} onChange={e => setForm({ ...form, branch: e.target.value })} data-testid="inward-branch"
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200">
                <option value="">Select Branch</option>
                {filterBranchesForUser(branches).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-700 mb-1">Inward Date *</label>
              <input type="date" value={form.inward_date} onChange={e => setForm({ ...form, inward_date: e.target.value })} data-testid="inward-date"
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-700 mb-1">Notes</label>
              <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes..." data-testid="inward-notes"
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
            </div>
          </div>

          {/* Purchase Invoice / Attachment Upload */}
          <div className="mb-5">
            <label className="block text-[11px] font-medium text-gray-700 mb-2">Purchase Invoice / Attachments <span className="text-red-500">*</span></label>
            <div className="flex flex-wrap items-center gap-2">
              {attachments.map((att, idx) => (
                <div key={idx} className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg">
                  <Paperclip className="w-3 h-3 text-gray-400" />
                  <span className="text-xs text-gray-700 max-w-[160px] truncate">{att.filename}</span>
                  <span className="text-[9px] text-gray-400">{(att.size / 1024).toFixed(0)} KB</span>
                  <button onClick={() => removeAttachment(idx)} className="p-0.5 hover:bg-red-50 rounded" data-testid={`remove-attachment-${idx}`}>
                    <X className="w-3 h-3 text-red-400" />
                  </button>
                </div>
              ))}
              <label className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 border-dashed rounded-lg cursor-pointer hover:bg-blue-100 transition-colors" data-testid="inward-upload-btn">
                <Upload className="w-3.5 h-3.5" />
                {uploading ? 'Uploading...' : 'Upload Invoice'}
                <input type="file" className="hidden" multiple accept=".pdf,.jpg,.jpeg,.png,.webp,.xlsx,.xls,.doc,.docx" onChange={handleFileUpload} />
              </label>
            </div>
            <p className="text-[10px] text-gray-400 mt-1">PDF, images, Excel or Word (max 10MB each)</p>
          </div>

          {/* Items Table */}
          {form.items.length > 0 ? (
            <>
            {/* Freight & Charges Section */}
            <div className="bg-amber-50/50 border border-amber-100 rounded-xl p-4 mb-5" data-testid="freight-charges-section">
              <div className="flex items-center gap-2 mb-3">
                <DollarSign className="w-4 h-4 text-amber-600" />
                <h3 className="text-xs font-bold text-gray-900">Freight & Charges (Landed Cost)</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                <div>
                  <label className="text-[10px] text-gray-500 mb-0.5 block">Inward Freight</label>
                  <input type="number" value={form.inward_freight || ''} data-testid="inward-freight-input"
                    onChange={e => setForm(f => ({...f, inward_freight: parseFloat(e.target.value) || 0}))}
                    placeholder="0" className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg" />
                </div>
                <div>
                  <label className="text-[10px] text-gray-500 mb-0.5 block">Loading Charges</label>
                  <input type="number" value={form.loading_charges || ''} data-testid="loading-charges-input"
                    onChange={e => setForm(f => ({...f, loading_charges: parseFloat(e.target.value) || 0}))}
                    placeholder="0" className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg" />
                </div>
                <div>
                  <label className="text-[10px] text-gray-500 mb-0.5 block">Unloading Charges</label>
                  <input type="number" value={form.unloading_charges || ''} data-testid="unloading-charges-input"
                    onChange={e => setForm(f => ({...f, unloading_charges: parseFloat(e.target.value) || 0}))}
                    placeholder="0" className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg" />
                </div>
                <div className="flex items-end">
                  <button onClick={() => setForm(f => ({...f, additional_charges: [...f.additional_charges, {name: '', amount: 0}]}))}
                    className="px-3 py-1.5 text-[10px] font-semibold text-amber-700 bg-amber-100 rounded-lg hover:bg-amber-200" data-testid="add-charge-btn">
                    + Add Charge
                  </button>
                </div>
              </div>

              {/* Dynamic Additional Charges */}
              {form.additional_charges.length > 0 && (
                <div className="space-y-2 mb-3">
                  {form.additional_charges.map((ch, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input value={ch.name} onChange={e => {
                        const arr = [...form.additional_charges]; arr[idx] = {...arr[idx], name: e.target.value}; setForm(f => ({...f, additional_charges: arr}));
                      }} placeholder="Charge name (e.g. Custom Duty, CFS)" data-testid={`charge-name-${idx}`}
                        className="flex-1 px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg" />
                      <input type="number" value={ch.amount || ''} onChange={e => {
                        const arr = [...form.additional_charges]; arr[idx] = {...arr[idx], amount: parseFloat(e.target.value) || 0}; setForm(f => ({...f, additional_charges: arr}));
                      }} placeholder="Amount" data-testid={`charge-amount-${idx}`}
                        className="w-28 px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg" />
                      <button onClick={() => {
                        setForm(f => ({...f, additional_charges: f.additional_charges.filter((_, i) => i !== idx)}));
                      }} className="p-1 hover:bg-red-50 rounded" data-testid={`remove-charge-${idx}`}>
                        <X className="w-3 h-3 text-red-400" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Landed Cost Summary */}
              {(() => {
                const isImportPO = form.po_type === 'import' && form.po_currency !== 'INR';
                const poCurrency = form.po_currency || 'INR';
                const exchangeRate = form.exchange_rate || 1;
                const totalQty = form.items.reduce((a, i) => a + (i.inward_qty || 0), 0);
                const totalCharges = (form.inward_freight || 0) + (form.loading_charges || 0) + (form.unloading_charges || 0) + form.additional_charges.reduce((a, c) => a + (c.amount || 0), 0);
                const chargePerUnit = totalQty > 0 ? totalCharges / totalQty : 0;
                const materialCostForeign = form.items.reduce((a, i) => a + (i.cost_price || 0) * (i.inward_qty || 0), 0);
                const materialCostINR = isImportPO ? materialCostForeign * exchangeRate : materialCostForeign;
                const totalLanded = materialCostINR + totalCharges;

                return (
                  <div className="space-y-2">
                    {isImportPO && (
                      <div className="flex items-center gap-3 bg-blue-50 rounded-lg p-3 border border-blue-100 mb-2">
                        <div>
                          <label className="text-[10px] font-semibold text-blue-700 mb-0.5 block">Exchange Rate (1 {poCurrency} = ? INR)</label>
                          <input type="number" step="0.01" value={form.exchange_rate || ''} data-testid="inward-exchange-rate"
                            onChange={e => setForm(f => ({...f, exchange_rate: parseFloat(e.target.value) || 0}))}
                            placeholder="e.g. 84.50"
                            className="w-32 px-2.5 py-1.5 text-xs font-bold border border-blue-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white" />
                        </div>
                        <div className="text-[10px] text-blue-600">
                          <span className="font-medium">Material Cost ({poCurrency}):</span> <span className="font-bold">{poCurrency} {materialCostForeign.toLocaleString()}</span>
                          <span className="mx-2">×</span>
                          <span className="font-medium">{exchangeRate}</span>
                          <span className="mx-2">=</span>
                          <span className="font-bold text-blue-800">₹{materialCostINR.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                        </div>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-4 text-xs bg-white rounded-lg p-3 border border-amber-100">
                      <div><span className="text-gray-500">Material Cost:</span> <span className="font-bold">{isImportPO ? `${poCurrency} ${materialCostForeign.toLocaleString()}` : `₹${materialCostForeign.toLocaleString()}`}</span></div>
                      {isImportPO && (
                        <div><span className="text-gray-500">Material Cost (INR):</span> <span className="font-bold text-blue-700">₹{materialCostINR.toLocaleString(undefined, {maximumFractionDigits: 0})}</span></div>
                      )}
                      <div><span className="text-gray-500">Total Charges:</span> <span className="font-bold text-amber-700">₹{totalCharges.toLocaleString()}</span></div>
                      <div><span className="text-gray-500">Charges/Unit:</span> <span className="font-bold text-amber-700">₹{chargePerUnit.toFixed(2)}</span></div>
                      <div><span className="text-gray-500">Total Landed:</span> <span className="font-bold text-green-700">₹{totalLanded.toLocaleString(undefined, {maximumFractionDigits: 0})}</span></div>
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="border border-gray-100 rounded-lg overflow-hidden mb-5">
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    {['Product', 'Unit', 'Ordered', 'Received', 'Rest Qty', 'Inward Qty', 'Batch',
                      form.po_type === 'import' && form.po_currency !== 'INR' ? `Rate (${form.po_currency})` : 'Rate',
                      'Landed Cost/Unit'
                    ].map(h => (
                      <th key={h} className="text-left px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {form.items.map((item, idx) => {
                    const isImportPO = form.po_type === 'import' && form.po_currency !== 'INR';
                    const exchangeRate = form.exchange_rate || 1;
                    const totalQty = form.items.reduce((a, i) => a + (i.inward_qty || 0), 0);
                    const totalCharges = (form.inward_freight || 0) + (form.loading_charges || 0) + (form.unloading_charges || 0) + form.additional_charges.reduce((a, c) => a + (c.amount || 0), 0);
                    const chargePerUnit = totalQty > 0 ? totalCharges / totalQty : 0;
                    const rateINR = isImportPO ? (item.cost_price || 0) * exchangeRate : (item.cost_price || 0);
                    const landed = rateINR + chargePerUnit;
                    const poCurrency = form.po_currency || 'INR';
                    return (
                    <tr key={idx} className="border-t border-gray-50">
                      <td className="px-3 py-2 font-medium text-gray-900">{item.product_name}</td>
                      <td className="px-3 py-2 text-gray-600">{item.unit}</td>
                      <td className="px-3 py-2 text-gray-700 font-semibold">{item.ordered_qty}</td>
                      <td className="px-3 py-2 text-blue-600">{item.previously_received}</td>
                      <td className="px-3 py-2 text-amber-600 font-semibold">{item.rest_qty}</td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          value={item.inward_qty}
                          onChange={e => updateItemQty(idx, 'inward_qty', Math.min(Number(e.target.value), item.rest_qty))}
                          max={item.rest_qty}
                          min={0}
                          data-testid={`inward-qty-${idx}`}
                          className="w-20 px-2 py-1 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-green-200 font-bold text-green-700"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          value={item.batch || ''}
                          onChange={e => updateItemQty(idx, 'batch', e.target.value)}
                          placeholder="Batch #"
                          data-testid={`inward-batch-${idx}`}
                          className="w-24 px-2 py-1 text-xs border border-gray-200 rounded-md font-mono"
                        />
                      </td>
                      <td className="px-3 py-2 text-gray-600">{isImportPO ? `${poCurrency} ` : '₹'}{item.cost_price?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-green-700 font-bold">₹{landed.toFixed(2)}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          ) : form.po_reference ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg mb-5">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto mb-2" />
              <p className="text-xs text-gray-500">Loading PO items...</p>
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg mb-5">
              <FileText className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-xs text-gray-500">Select a Purchase Order to load items</p>
            </div>
          )}

          {/* Summary + Submit */}
          {form.items.length > 0 && (
            <div className="flex items-center justify-between bg-blue-50/50 rounded-lg p-3 mb-5">
              <div className="flex gap-6 text-xs">
                <div><span className="text-gray-500">Total Items:</span> <span className="font-bold text-gray-900">{form.items.length}</span></div>
                <div><span className="text-gray-500">Items with Qty:</span> <span className="font-bold text-green-700">{form.items.filter(i => i.inward_qty > 0).length}</span></div>
                <div><span className="text-gray-500">Total Inward Qty:</span> <span className="font-bold text-gray-900">{form.items.reduce((a, i) => a + (i.inward_qty || 0), 0)}</span></div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
            <button onClick={() => { setView('list'); resetForm(); }} className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
            <button data-testid="submit-inward-btn" onClick={handleSubmit} disabled={submitting}
              className="flex items-center gap-2 px-5 py-2 text-xs font-semibold text-white rounded-lg hover:shadow-lg disabled:opacity-50" style={{ backgroundColor: '#24549c' }}>
              {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Create Inward
            </button>
          </div>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="p-5 lg:p-6" data-testid="inward-module">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Inwards</h1>
          <p className="text-xs text-gray-500 mt-0.5">Goods receipt against Purchase Orders</p>
        </div>
        <button onClick={() => setView('create')} data-testid="new-inward-btn"
          className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white rounded-lg hover:shadow-lg" style={{ backgroundColor: '#24549c' }}>
          <Plus className="w-3.5 h-3.5" /> New Inward
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        {[
          { label: 'Total', value: stats.total, color: '#24549c', icon: ArrowDownToLine },
          { label: 'Completed', value: stats.completed, color: '#059669', icon: CheckCircle },
          { label: 'Cancelled', value: stats.cancelled, color: '#dc2626', icon: XCircle },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: s.color + '15' }}>
                <s.icon className="w-3.5 h-3.5" style={{ color: s.color }} />
              </div>
              <span className="text-[11px] font-medium text-gray-500">{s.label}</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search inward #, PO #, vendor..." data-testid="inward-search"
            className="w-full pl-9 pr-4 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
      ) : inwards.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
          <ArrowDownToLine className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">No inward entries found</p>
          <p className="text-xs text-gray-400 mt-1">Create your first inward against a Purchase Order</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <SortableHeader label="Inward #" sortKey="inward_number" currentSort={sortConfig} onSort={handleSort} />
                <SortableHeader label="PO Number" />
                <SortableHeader label="Vendor" />
                <SortableHeader label="Branch" />
                <SortableHeader label="Items" align="center" />
                <SortableHeader label="Total Qty" align="center" />
                <SortableHeader label="Inward Date" sortKey="inward_date" currentSort={sortConfig} onSort={handleSort} />
                <SortableHeader label="Status" />
                <SortableHeader label="Invoice" />
                <SortableHeader label="" />
              </tr>
            </thead>
            <tbody>
              {inwards.map(inw => (
                <tr key={inw.id} className="border-b border-gray-50 hover:bg-gray-50/50 cursor-pointer" onClick={() => viewDetail(inw.id)} data-testid={`inward-row-${inw.id}`}>
                  <td className="px-4 py-3 font-mono font-semibold text-gray-900">{inw.inward_number}</td>
                  <td className="px-4 py-3 text-blue-700 font-mono text-[10px]">{inw.po_number}</td>
                  <td className="px-4 py-3 text-gray-700">{inw.vendor_name}</td>
                  <td className="px-4 py-3 text-gray-500 text-[10px]">{inw.branch_name || '—'}</td>
                  <td className="px-4 py-3 text-center">{inw.items?.length || 0}</td>
                  <td className="px-4 py-3 text-center font-semibold">{(inw.items || []).reduce((a, i) => a + (i.inward_qty || 0), 0)}</td>
                  <td className="px-4 py-3 text-gray-500">{inw.inward_date}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full ${inw.status === 'Completed' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {inw.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {inw.purchase_invoice_number ? (
                      <span className="inline-flex px-2 py-0.5 text-[9px] font-semibold rounded-full bg-blue-100 text-blue-700" data-testid={`inward-invoiced-${inw.id}`}>Invoiced</span>
                    ) : (
                      <span className="inline-flex px-2 py-0.5 text-[9px] font-semibold rounded-full bg-amber-100 text-amber-600">Pending</span>
                    )}
                  </td>
                  <td className="px-4 py-3"><Eye className="w-3.5 h-3.5 text-gray-400 mx-auto" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {inwards.length > 0 && <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={handlePageSizeChange} />}
    </div>
  );
};

export default InwardModule;
